import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { SftpImportConfigurationWorkflow, ImportHierarchyOption } from '../../../store/Models/Api/SftpImportModels';
import { AppServices } from '../../../AppServices';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { ColumnMappingHelper } from '../helpers/ColumnMappingHelper';
import { ValidationErrorPopup } from '../components/ValidationErrorPopup';

interface ColumnMappingFileUploadSectionLocalProps {
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
}

interface ColumnMappingFileUploadSectionStateProps {
    appServices: AppServices;
}

type ColumnMappingFileUploadSectionProps = ColumnMappingFileUploadSectionLocalProps & ColumnMappingFileUploadSectionStateProps & ToxnotComponentProperties;

interface ColumnMappingFileUploadSectionState {
    isUploading: boolean;
    showErrorPopup: boolean;
    errorMessage: string | null;
    errorHeader: string | null;
    extractedColumns: string[];
}

class ColumnMappingFileUploadSectionComponent extends ToxnotComponent<ColumnMappingFileUploadSectionProps, ColumnMappingFileUploadSectionState> {
    private serviceClient = new ToxnotServiceClient();
    
    constructor(props: ColumnMappingFileUploadSectionProps) {
        super(props);
        this.state = {
            isUploading: false,
            showErrorPopup: false,
            errorMessage: null,
            errorHeader: null,
            extractedColumns: []
        };
    }

    private closeErrorPopup = () => {
        this.setState({
            showErrorPopup: false,
            errorMessage: null,
            errorHeader: null
        });
    };

    private handleMapColumnsClick = async () => {
        const fileInput = document.getElementById('new-inventory-import-file-picker') as HTMLInputElement;
        const file = fileInput?.files?.[0];
        const { configData } = this.props;
        
        if (!file) {
            this.setState({ 
                showErrorPopup: true,
                errorMessage: this.localizeString('Please select a file first'),
                errorHeader: this.localizeString('No File Selected')
            });
            return;
        }

        this.setState({ 
            isUploading: true, 
            showErrorPopup: false,
            errorMessage: null, 
            errorHeader: null,
            extractedColumns: [] // Clear previous results
        });

        try {
            const response = await this.serviceClient.SftpExtractSpreadsheetColumns(
                file,
                configData.sheetName,
                configData.headerEndRow == undefined ? 0 : configData.headerEndRow - 1
            );
            
            if (response.result === "Ok" && response.data as string[]) {
                this.setState({ 
                    extractedColumns: response.data || [],
                    isUploading: false 
                });

                if(this.state.extractedColumns.length === 0) {
                    this.setState({ 
                        showErrorPopup: true,
                        errorMessage: this.localizeString('No columns were found in the uploaded file. Please check the file and try again.'),
                        errorHeader: this.localizeString('No Columns Found')
                    });

                    return;
                }

                var emptyHeaders = this.state.extractedColumns.filter(h => !h || h.trim() === '');

                if(emptyHeaders.length > 0) {
                    var nonEmptyHeaders = this.state.extractedColumns.filter(h => h && h.trim() !== '');
                    
                    this.setState({ 
                        extractedColumns: nonEmptyHeaders,
                        showErrorPopup: true,
                        errorMessage: this.localizeString('Some extracted columns are empty and wont be mapped.'),
                        errorHeader: this.localizeString('Empty Columns Found')
                    });
                }

                // Apply extracted columns to mappings
                this.applyExtractedColumns(this.state.extractedColumns);
            }
            else {
                this.setState({ 
                    isUploading: false, 
                    showErrorPopup: true,
                    errorMessage: this.localizeString(response.message || 'Mapping from file failed'),
                    errorHeader: this.localizeString('Mapping Failed')
                });
            }
        } catch (error: any) {
            this.setState({ 
                isUploading: false, 
                showErrorPopup: true,
                errorMessage: this.localizeString('Mapping from file failed'),
                errorHeader: this.localizeString('Mapping Failed')
            });
        }
    };

    private applyExtractedColumns = async (columnNames: string[]) => {
        const { configData, onConfigChange, appServices } = this.props;
        
        // Get extensible columns from BOM template
        const extensibleColumns = await ColumnMappingHelper.getExtensibleColumns();
        
        // Get available mapping options to match against
        const mappingOptions = ColumnMappingHelper.getMappingOptions(configData, appServices, extensibleColumns);
        
        // Track used column IDs to avoid duplicates within this extraction
        const usedColumnIds: string[] = [];
        
        // Create new mappings from extracted columns with intelligent matching
        const newMappings = columnNames.map(columnName => {
            const matchedMapping = ColumnMappingHelper.findBestColumnMatch(
                columnName, 
                mappingOptions, 
                usedColumnIds,
                extensibleColumns
            );
            
            // Track this column ID to prevent duplicates in subsequent mappings
            if (matchedMapping && matchedMapping !== '--tn-skip') {
                usedColumnIds.push(matchedMapping);
            }
            
            return {
                headerName: columnName,
                columnId: matchedMapping || '--tn-skip', // Use matched mapping or default to skip
                level: configData.importHierarchyOptionType === ImportHierarchyOption.RepeatedMaterialIdentifiers ? 1 : null
            };
        });

        onConfigChange({ 
            spreadsheetHeadersToColumnIdMappings: newMappings 
        });
    };

    render() {
        const { isUploading, showErrorPopup, errorMessage, errorHeader, extractedColumns } = this.state;

        return (
            <div className="alert alert-light" style={{ marginBottom: '15px' }}>
                <h4>{this.localizeString("Extract Columns from Sample Spreadsheet")}</h4>
                <p>{this.localizeString("Upload a spreadsheet file to automatically extract column headers for mapping.")}</p>
                
                <div id="main-file-picker-form-group" className="form-group" style={{ marginBottom: '10px' }}>
                    <label id="main-file-picker-label" htmlFor="new-inventory-import-file-picker">{this.localizeString("Source Spreadsheet File")}</label>
                    <p id="main-file-picker-sub-label">{this.localizeString("CSV or XLSX")}</p>
                    <input
                        type="file"
                        id="new-inventory-import-file-picker"
                        accept=".csv,.xlsx"
                        className="form-control"
                        disabled={isUploading}
                    />
                </div>
                
                <button 
                    type="button" 
                    id="start-upload-button" 
                    className="btn btn-toxnot-primary" 
                    style={{ display: 'inline-block' }}
                    onClick={this.handleMapColumnsClick}
                    disabled={isUploading}
                >
                    {isUploading 
                        ? this.localizeString("Mapping...") 
                        : this.localizeString("Map File Columns")
                    }
                </button>

                {extractedColumns.length > 0 && (
                    <div className="alert alert-success" style={{ marginTop: '10px' }}>
                        {this.localizeString(`Successfully extracted ${extractedColumns.length} columns from spreadsheet`)}
                    </div>
                )}

                {/* Error Popup */}
                <ValidationErrorPopup 
                    show={showErrorPopup}
                    message={errorMessage || ''}
                    header={errorHeader}
                    onClose={this.closeErrorPopup}
                />
            </div>
        );
    }
}

const ColumnMappingFileUploadSection = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ColumnMappingFileUploadSectionComponent);

export default ColumnMappingFileUploadSection;